import { useEffect, useState, useCallback, useRef } from "react";
import useAuth from "../context/AuthProvider";
import { YearToDateReportModel, YearToDateReportModel_Resp } from "../models/YearToDateReportModel";
import { Box, LinearProgress, TextField, Grid, FormControl } from "@mui/material";
import { DataGridPro, GridColDef, GridFilterModel, GridSortModel } from "@mui/x-data-grid-pro";
import { apiClient } from "../helper/api";
import moment from "moment";
import { RiExpandUpDownLine } from "react-icons/ri";
import Swal from "sweetalert2";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { getRowsPerPageOptions } from "../components/AddEdit/CustomFunctions";
import { writeXlsxFile } from "../helper/utils";

export const YearToDateReport = () => {
  const { user } = useAuth();
  const token = user?.token;
  const [loading, setLoading] = useState(false);
  
  // Ref to hold AbortController for cancelling pending requests
  const controllerRef = useRef<AbortController | null>(null);
  
  const initValue: YearToDateReportModel_Resp = {
    items: [],
    totalCount: 0,
    pageSize: 25,
    pageCount: 0,
    currentPage: 0,
  };

  const [data, setData] = useState<YearToDateReportModel[]>(initValue.items);
  const [totalCount, setTotalCount] = useState(initValue.totalCount);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  const fetchData = useCallback(async () => {
    // CANCEL previous pending request
    if (controllerRef.current) {
      controllerRef.current.abort();
    }

    // Create a new controller for this request
    const currentController = new AbortController();
    controllerRef.current = currentController;
    const signal = currentController.signal;

    setLoading(true);
    
    try {
      const sortField = sortModel.length > 0 ? sortModel[0].field : 'dateFiled';
      const sortDirection = sortModel.length > 0 ? sortModel[0].sort || 'desc' : 'desc';
      
      const requestBody: any = {
        pageNumber: currentPage + 1,
        pageSize: pageSize,
        field: sortField,
        sort: sortDirection,
        filterModel: filterModel,
      };
      if (startDate) {
        requestBody.startDate = startDate.format('YYYY-MM-DD');
      }
      if (endDate) {
        requestBody.endDate = endDate.format('YYYY-MM-DD');
      }

      const res = await apiClient(`/YearToDateReport/getYearToDateReport`, {
        method: "POST",
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }),
        body: JSON.stringify(requestBody),
        signal, // IMPORTANT: Pass abort signal
      });
      
      // If request was aborted, do not update state
      if (!res) {
        return;
      }
      
      if (res) {
        // API returns: { data: Array, pagination: { totalRecords, currentPage, pageSize, totalPages } }
        // Note: toCamel converts PascalCase to camelCase (RecordId -> recordId, Client -> client, etc.)
        const responseData = res.data || [];
        const totalRecords = res.totalRecords || 0;
        const currentPage = res.currentPage;
        
        // Set currentPage from API response if available
        // Convert from 1-based (API) to 0-based (DataGrid) if needed
        if (currentPage !== undefined && currentPage !== null) {
          // API typically uses 1-based pagination, DataGrid uses 0-based
          const dataGridPage = currentPage > 0 ? currentPage - 1 : 0;
          setCurrentPage(dataGridPage);
        }
        
        // Ensure data is an array
        if (Array.isArray(responseData) && responseData.length > 0) {
          setData(responseData);
          setTotalCount(totalRecords);
        } else {
          setData([]);
          setTotalCount(0);
        }
      } else {
        setData([]);
        setTotalCount(0);
      }
    } catch (error: any) {
      // If request was aborted, do not show error or update state
      if (error.name === "AbortError") {
        return;
      }
      console.error("Error fetching Year to Date Report data:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load Year to Date Report data',
        confirmButtonText: 'OK'
      });
    } finally {
      if (controllerRef.current === currentController) {
        setLoading(false);
      }
    }
  }, [currentPage, pageSize, filterModel, sortModel, startDate, endDate, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Cleanup: Cancel any pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
        controllerRef.current = null;
      }
    };
  }, []);

  const handleFilterModelChange = useCallback((newFilterModel: GridFilterModel) => {
    setFilterModel(newFilterModel);
    setCurrentPage(0);
  }, []);

  const handleSortModelChange = useCallback((newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
    setCurrentPage(0);
  }, []);

  const columns: GridColDef[] = [
    {
      field: 'client',
      type: 'text',
      headerName: 'Client',
      flex: 1,
      minWidth: 150,
      headerAlign: 'center',
      align: 'center',
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
    },
    {
      field: 'state',
      type: 'text',
      headerName: 'State',
      flex: 1,
      minWidth: 120,
      headerAlign: 'center',
      align: 'center',
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
    },
    {
      field: 'invoiceNumber',
      type: 'text',
      headerName: 'Invoice Number',
      flex: 1,
      minWidth: 160,
      headerAlign: 'center',
      align: 'center',
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
    },
    {
      field: 'insuredName',
      type: 'text',
      headerName: 'Insured Name',
      flex: 1,
      minWidth: 180,
      headerAlign: 'center',
      align: 'center',
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
    },
    {
      field: 'policyNumber',
      type: 'text',
      headerName: 'Policy Number',
      flex: 1,
      minWidth: 160,
      headerAlign: 'center',
      align: 'center',
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
    },
    {
      field: 'dateFiled',
      type: 'date',
      headerName: 'Date Filed',
      flex: 1,
      minWidth: 140,
      headerAlign: 'center',
      align: 'center',
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      valueGetter: (params) => {
        if (!params.value) return null;
        return new Date(params.value);
      },
      renderCell: (params) => (
        <div>
          {params.value ? moment(params.value).format("MM/DD/YYYY") : ""}
        </div>
      ),
    },
    {
      field: 'transactionType',
      type: 'text',
      headerName: 'Transaction Type',
      flex: 1,
      minWidth: 180,
      headerAlign: 'center',
      align: 'center',
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      renderCell: (params) => (
        <div>
          {params.value || ''}
        </div>
      ),
    },
    {
      field: 'reportName',
      type: 'text',
      headerName: 'Report Name',
      flex: 2,
      minWidth: 250,
      headerAlign: 'center',
      align: 'left',
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      renderCell: (params) => (
        <div style={{ 
          whiteSpace: 'normal', 
          wordWrap: 'break-word', 
          overflow: 'visible',
          textAlign: 'left',
          width: '100%'
        }}>
          {params.value || ''}
        </div>
      ),
    },
  ];

  const handleStartDateChange = (newValue: unknown) => {
    setStartDate(newValue as Dayjs | null);
    setCurrentPage(0);
  };

  const handleEndDateChange = (newValue: unknown) => {
    setEndDate(newValue as Dayjs | null);
    setCurrentPage(0);
  };

  const handleClearDates = () => {
    setStartDate(null);
    setEndDate(null);
    setCurrentPage(0);
  };

  const fetchAllDataForExport = async () => {
    const sortField = sortModel.length > 0 ? sortModel[0].field : 'dateFiled';
    const sortDirection = sortModel.length > 0 ? sortModel[0].sort || 'desc' : 'desc';
    
    // Use totalCount if available, otherwise use a large number to fetch all records
    const pageSizeForExport = totalCount > 0 ? totalCount : 100000;
    
    const requestBody: any = {
      pageNumber: 1,
      pageSize: pageSizeForExport,
      field: sortField,
      sort: sortDirection,
      filterModel: filterModel,
    };
    if (startDate) {
      requestBody.startDate = startDate.format('YYYY-MM-DD');
    }
    if (endDate) {
      requestBody.endDate = endDate.format('YYYY-MM-DD');
    }

    const res = await apiClient(`/YearToDateReport/getYearToDateReport`, {
      method: "POST",
      headers: new Headers({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      }),
      body: JSON.stringify(requestBody)
    });

    if (res && res.data) {
      return res.data || [];
    }
    return [];
  };

  const handleExportXlsx = async () => {
    try {
      setLoading(true);
      const allData = await fetchAllDataForExport();
      
      if (!allData || allData.length === 0) {
        Swal.fire({
          icon: 'info',
          title: 'No Data',
          text: 'No data available to export',
          confirmButtonText: 'OK'
        });
        setLoading(false);
        return;
      }

      const fieldsToExport: (keyof YearToDateReportModel)[] = [
        'client', 'state', 'invoiceNumber', 'insuredName', 
        'policyNumber', 'dateFiled', 'transactionType', 'reportName'
      ];

      const capitalizeFirstLetter = (str: string) => {
        return str.charAt(0).toUpperCase() + str.slice(1).replace(/([A-Z])/g, ' $1').trim();
      };

      const formattedValues = allData.map((item: YearToDateReportModel) => {
        return fieldsToExport.map((key: keyof YearToDateReportModel) => {
          const value = item[key];
          if (key === 'dateFiled' && value) {
            return moment(value).format("MM/DD/YYYY");
          }
          return value || '';
        });
      });

      const headers = fieldsToExport.map(field => capitalizeFirstLetter(field));
      // Add title row
      const titleRow = new Array(fieldsToExport.length).fill('');
      titleRow[0] = 'Year to Date Report';
      
      // Add summary row for transaction total
      const summaryRow = new Array(fieldsToExport.length).fill('');
      summaryRow[0] = 'Transaction Total';
      summaryRow[1] = allData.length.toLocaleString();

      const exportData = [
        titleRow,
        summaryRow, 
        [],
        headers,
        ...formattedValues 
      ];

      writeXlsxFile(exportData, "YearToDateReport");
    } catch (error) {
      console.error("Error exporting data:", error);
      Swal.fire({
        icon: 'error',
        title: 'Export Error',
        text: 'Failed to export data',
        confirmButtonText: 'OK'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ width: '100%', height: '100%' }}>
        <div style={{ position: "fixed", top: "0", left: "0", zIndex: "9990", width: "100%" }}>
          {loading && <LinearProgress color="info" sx={{ backgroundColor: "#132D3D" }} />}
        </div>
        
        <Box sx={{ mb: 1, p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="card-title">
                    <span className="d-flex">Year to Date Report</span>
                </div>
          </div>
          {/* Date Range Pickers */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControl>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={handleStartDateChange}
                inputFormat="MM/DD/YYYY"
                maxDate={endDate || undefined}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Start Date"
                    sx={{
                      width: '200px',
                      '& .MuiOutlinedInput-root': {
                        height: '40px',
                      },
                    }}
                  />
                )}
              />
            </FormControl>
            <FormControl>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={handleEndDateChange}
                inputFormat="MM/DD/YYYY"
                minDate={startDate || undefined}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="End Date"
                    sx={{
                      width: '200px',
                      '& .MuiOutlinedInput-root': {
                        height: '40px',
                      },
                    }}
                  />
                )}
              />
            </FormControl>
            {(startDate || endDate) && (
              <button
                onClick={handleClearDates}
                className="active-btn-blue"
                style={{ height: '40px', padding: '0 20px' }}
              >
                Clear
              </button>
            )}
            {(user?.userRoles.split(",").includes("Super Admin") || 
              user?.userRoles.split(",").includes("Customer Admin")) && (
              <button
                onClick={handleExportXlsx}
                className="active-btn-blue-export-12"
                style={{ height: '40px', padding: '0 20px' }}
                title="Export"
              >
                Export
              </button>
            )}
          </Box>
        </Box>

        <div style={{ height: "90%", overflowX: "scroll" }}>
          <DataGridPro
            rows={data || []}
            columns={columns}
            pagination
            page={currentPage}
            pageSize={pageSize}
            rowCount={totalCount}
            rowsPerPageOptions={getRowsPerPageOptions(totalCount, pageSize)}
            paginationMode="server"
            filterMode="server"
            sortingMode="server"
            sortingOrder={['asc', 'desc']}
            onPageChange={(newPage) => setCurrentPage(newPage)}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setCurrentPage(0);
            }}
            onFilterModelChange={handleFilterModelChange}
            onSortModelChange={handleSortModelChange}
            filterModel={filterModel}
            sortModel={sortModel}
            disableSelectionOnClick
            style={{ textAlign: "center" }}
            getRowId={(row) => {
              // Generate a unique ID for each row
              const id = row.recordId || row.id || `${row.recordType || 'record'}-${row.invoiceNumber || row.reportName || ''}-${row.policyNumber || ''}-${row.client || ''}-${row.state || ''}`;
              return id;
            }}
            sx={{
              border: "0px",
              fontSize: "16px",
              '& .MuiDataGrid-columnHeaderTitle': {
                overflow: 'visible',
                whiteSpace: 'normal',
                lineHeight: 'normal',
                wordWrap: 'break-word',
                textOverflow: 'clip',
              },
              '& .MuiDataGrid-cell': {
                overflow: 'visible',
                whiteSpace: 'normal',
                lineHeight: 'normal',
                wordWrap: 'break-word',
                textOverflow: 'clip',
              },
            }}
          />
        </div>
    </Box>
    </LocalizationProvider>
  );
};

